'use client';
import * as React from 'react';
import { Chip, Typography } from '@mui/material';
import { PricingPlan } from '../../types/pricing-plan';
import { CallToActionButton } from './call-to-action-button';
import { EventBus } from '@/shared/utils';
import {
  DynamicPageEventKeys,
  DynamicPageEvents,
} from 'src/components/containers/dynamic-page/events';
import { SubscriptionsOptions } from '../../types/subscription';
import { CheckoutSubscription } from 'src/components/containers/dynamic-page/types';
import { MarkdownRenderer } from 'src/shared/components/markdown-renderer';

type PricingPlanProps = PricingPlan & {
  eventBus: EventBus<DynamicPageEvents>;
  subscriptionOptions: SubscriptionsOptions | null;
  setModalOpen: (values: CheckoutSubscription) => void;
  isLogged: boolean;
};

const PricingCard: React.FC<PricingPlanProps> = ({
  title,
  price,
  stripeCode,
  originalPrice,
  description,
  buttonText,
  disclaimer,
  isPopular,
  discountCode,
  variant,
  badge,
  callToAction,
  eventBus,
  subscriptionOptions,
  setModalOpen,
  isLogged,
}) => {
  const cardClass = variant === 'highlighted' ? 'bg-primary-main' : 'bg-paper';
  const textClass = variant === 'highlighted' ? 'text-primary-contrastText' : 'text-text-secondary';
  const titleClass = variant === 'highlighted' ? 'text-primary-contrastText' : 'text-secondary';
  const discountClass = variant === 'highlighted' ? 'bg-paper' : 'bg-neutral-main';

  const currentSubscriptionOption = subscriptionOptions?.find(
    (el) => el.priceId === (stripeCode || ''),
  );

  return (
    <article
      className={`flex flex-col flex-1 shrink p-5 lg:p-8 rounded-2xl ${cardClass} h-full m-auto max-w-[380px] lg:max-w-full`}
    >
      <header className="flex flex-col justify-center w-full">
        <div className="flex flex-wrap gap-2 justify-between items-center">
          <Typography
            component="h5"
            variant="h5"
            className={`flex-1 font-bold leading-none ${titleClass}`}
          >
            {title}
          </Typography>
          {!!badge && (
            <Chip
              label={<Typography variant="button-s">{badge}</Typography>}
              size="medium"
              variant="filled"
              className={`font-medium ${
                variant === 'highlighted' ? 'bg-primary-dark' : 'bg-success-light text-paper'
              }`}
            />
          )}
        </div>
        <div className="flex flex-col self-start mt-4">
          <div className="flex gap-2 items-end self-start font-bold whitespace-nowrap">
            <Typography
              variant="work-sans-h1"
              component="h1"
              color="textPrimaryContrastText"
              className="leading-none"
            >
              €{price}
            </Typography>
            {originalPrice && (
              <Typography
                variant="h5"
                className="line-through leading-none mb-[4px]"
                color="textPrimaryContrastText"
              >
                €{originalPrice}
              </Typography>
            )}
          </div>
          <Typography variant="body-500" component="p" className={`mt-2 ${textClass}`}>
            <MarkdownRenderer markdown={description} color="textPrimaryContrastText" />
          </Typography>
        </div>
      </header>
      <footer className="flex flex-col mt-8 w-full">
        <div className="flex flex-col w-full">
          {!!callToAction && (
            <CallToActionButton
              button={callToAction}
              size="large"
              color={variant === 'highlighted' ? 'inherit' : 'primary'}
              className={`w-full ${variant === 'highlighted' ? 'text-primary-main shadow-none' : ''}`}
              onClick={() => {
                if (!currentSubscriptionOption?.priceId) return;

                const subData = {
                  priceId: currentSubscriptionOption.priceId,
                  name: currentSubscriptionOption.name,
                  price: currentSubscriptionOption.price,
                  trialPeriodDays: currentSubscriptionOption.trialPeriodDays,
                  couponName: discountCode || null,
                };
                if (isLogged) eventBus.emit(DynamicPageEventKeys.checkout.getSubscription, subData);
                else setModalOpen(subData);
              }}
            />
          )}
          <Typography variant="body-small-500" component="p" className={`${textClass} mt-4`}>
            {disclaimer}
          </Typography>
        </div>
        {discountCode && (
          <Chip
            label={`CODICE SCONTO: ${discountCode}`}
            size="medium"
            variant="filled"
            className={`mt-6 w-full ${discountClass}`}
          />
        )}
      </footer>
    </article>
  );
};

export default PricingCard;
